import React, { useState, useRef } from "react";
import { Mail, MessageSquare, Phone, Smartphone, Users, FileText, Camera, Loader2, X } from "lucide-react";
import { avatarUrl, api } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { toast } from "sonner";

export const COMM_TYPES = [
  { value: "email", label: "Email", icon: Mail },
  { value: "message", label: "Message", icon: MessageSquare },
  { value: "call", label: "Call", icon: Phone },
  { value: "sms", label: "SMS", icon: Smartphone },
  { value: "meeting", label: "Meeting", icon: Users },
];

export function CommIcon({ type, className = "h-4 w-4" }) {
  const t = COMM_TYPES.find((c) => c.value === type);
  const Icon = t ? t.icon : FileText;
  return <Icon className={className} />;
}

const STATUS_STYLES = {
  active: { cls: "bg-[#EDE7FF] text-[#5b2fd6]", dot: "#723FFF" },
  actioned: { cls: "bg-[#30BD79]/12 text-[#1f8f5a]", dot: "#30BD79" },
  overdue: { cls: "bg-[#F95B5F]/12 text-[#d83a3f]", dot: "#F95B5F" },
  submitted: { cls: "bg-[#EDE7FF] text-[#5b2fd6]", dot: "#723FFF" },
  in_progress: { cls: "bg-[#3B82F6]/12 text-[#2563eb]", dot: "#3B82F6" },
  on_hold: { cls: "bg-[#F59E0B]/14 text-[#b45309]", dot: "#F59E0B" },
  awaiting_information: { cls: "bg-[#F95B5F]/12 text-[#d83a3f]", dot: "#F95B5F" },
  closed: { cls: "bg-[#30BD79]/12 text-[#1f8f5a]", dot: "#30BD79" },
};

const STATUS_LABELS = {
  in_progress: "In Progress",
  on_hold: "On Hold",
  awaiting_information: "Awaiting Info",
};

export function StatusBadge({ status, testid }) {
  const s = STATUS_STYLES[status] || { cls: "bg-[#f1f2f4] text-gray-500", dot: "#9FA1A4" };
  const label = STATUS_LABELS[status] || status;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-mono-data font-semibold uppercase tracking-wider ${s.cls}`}
    >
      <span className="dot" style={{ backgroundColor: s.dot }} />
      {label}
    </span>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-10 animate-fade-up">
      <div>
        <p className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#723FFF] mb-2.5">
          Mission Control
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]">{title}</h1>
        {subtitle && <p className="mt-2.5 text-sm text-[#9FA1A4] max-w-2xl leading-relaxed">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] py-20 px-6 text-center"
    >
      {Icon && (
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EDE7FF] text-[#723FFF]">
          <Icon className="h-6 w-6" />
        </div>
      )}
      <h3 className="font-display text-lg font-semibold text-[#06060C]">{title}</h3>
      {description && <p className="mt-2 text-sm text-[#9FA1A4] max-w-md leading-relaxed">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Avatar({ name, size = 28, account, userId, hasAvatar, version, className = "" }) {
  const [err, setErr] = useState(false);
  const initials = (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const bg = account === "client" ? "#723FFF" : "#06060C";
  const showImg = hasAvatar && userId && !err;
  return (
    <span
      className={`rounded-full text-white flex items-center justify-center shrink-0 font-medium overflow-hidden ${className}`}
      style={{ height: size, width: size, backgroundColor: bg, fontSize: size * 0.4 }}
      title={name}
    >
      {showImg ? (
        <img src={avatarUrl(userId, version)} alt={name} className="h-full w-full object-cover" onError={() => setErr(true)} />
      ) : initials}
    </span>
  );
}

// Super-admin control: click the avatar to upload/replace a user's photo (uses /admin/users/{id}/avatar).
export function AdminAvatarEditor({ user, size = 40, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(0);
  const fileRef = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/users/${user.id}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Updated ${user.name}'s photo`);
      setBust(Date.now());
      onChanged && onChanged();
    } catch (err) { toast.error("Could not upload image"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async () => {
    if (!(await confirmDelete({ title: "Remove this photo?", confirmText: "Remove" }))) return;
    setBusy(true);
    try { await api.delete(`/admin/users/${user.id}/avatar`); toast.success("Photo removed"); setBust(Date.now()); onChanged && onChanged(); }
    catch { toast.error("Could not remove image"); }
    finally { setBusy(false); }
  };
  return (
    <div className="relative shrink-0" style={{ height: size, width: size }}>
      <Avatar name={user.name} size={size} account={user.account} userId={user.id} hasAvatar={user.has_avatar}
              version={user.has_avatar ? `${user.avatar_version || ""}-${bust}` : bust} />
      <button type="button" data-testid={`admin-avatar-edit-${user.id}`} onClick={() => fileRef.current?.click()} disabled={busy}
              title="Change photo"
              className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-[#06060C] text-white flex items-center justify-center border-2 border-white hover:bg-[#1c1c24]">
        {busy ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Camera className="h-2.5 w-2.5" />}
      </button>
      {user.has_avatar && !busy && (
        <button type="button" data-testid={`admin-avatar-remove-${user.id}`} onClick={remove} title="Remove photo"
                className="absolute -top-1 -left-1 h-4 w-4 rounded-full bg-rose-500 text-white flex items-center justify-center border-2 border-white hover:bg-rose-600">
          <X className="h-2 w-2" />
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
    </div>
  );
}


export function OwnerChip({ label, name, account, userId, hasAvatar, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-2 min-w-0">
      <Avatar name={name || "Unassigned"} size={26} account={account} userId={userId} hasAvatar={hasAvatar} />
      <div className="min-w-0 leading-tight">
        <p className="font-mono-data text-[9px] uppercase tracking-wider text-[#c4c5cc]">{label}</p>
        <p className="text-xs text-[#06060C] truncate">{name || "Unassigned"}</p>
      </div>
    </div>
  );
}

export function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export const COMPANY_FIELDS = [
  { key: "entity_name", label: "Entity name", placeholder: "Registered company name" },
  { key: "trading_as", label: "Trading as", placeholder: "Trading / brand name" },
  { key: "contact_name", label: "Contact name", placeholder: "Primary contact" },
  { key: "email", label: "Email address", placeholder: "accounts@company.com", type: "email" },
  { key: "phone", label: "Phone", placeholder: "+61 ..." },
  { key: "abn", label: "ABN", placeholder: "00 000 000 000" },
  { key: "address", label: "Address", placeholder: "Street, city, state, postcode", textarea: true },
];

export function CompanyDetailsForm({ value, onChange, testid = "company" }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid={`${testid}-form`}>
      {COMPANY_FIELDS.map((f) => (
        <div key={f.key} className={f.textarea ? "sm:col-span-2" : ""}>
          <label className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-1.5 block">{f.label}</label>
          {f.textarea ? (
            <textarea data-testid={`${testid}-${f.key}`} value={v[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} rows={2}
              className="w-full rounded-md bg-[#F6F6FD] border border-[#EEEFEF] px-3 py-2 text-sm text-[#06060C] placeholder:text-[#c4c5cc] focus:outline-none focus:ring-2 focus:ring-[#723FFF]/30" />
          ) : (
            <input data-testid={`${testid}-${f.key}`} type={f.type || "text"} value={v[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder}
              className="w-full rounded-md bg-[#F6F6FD] border border-[#EEEFEF] px-3 py-2 text-sm text-[#06060C] placeholder:text-[#c4c5cc] focus:outline-none focus:ring-2 focus:ring-[#723FFF]/30" />
          )}
        </div>
      ))}
    </div>
  );
}
